import { Badge, Button, EmptyState, SkeletonRows } from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { CalendarDaysIcon } from 'lucide-react';
import { useState } from 'react';
import { useEventsQuery } from '../../../api/queries.ts';
import { CreateEventDialog } from '../../../components/events/create-event-dialog.tsx';
import { NotWired } from '../../../components/not-wired.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { formatDateTime } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/events/')({
  component: EventsPage,
  errorComponent: RouteErrorPanel,
});

function EventsPage() {
  const [creating, setCreating] = useState(false);
  const events = useEventsQuery();
  const items = events.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Events"
        description="Conferences, dinners and meetings you met people at. Calendar-derived events join these once sync lands."
      />
      <PageBody>
        {events.isLoading ? (
          <SkeletonRows rows={5} />
        ) : events.isError ? (
          <EmptyState kind="error" title="Could not load events" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<CalendarDaysIcon aria-hidden />}
            title="No events yet"
            description="An event is created when you add a contact at one, or when calendar sync promotes a meeting."
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {items.map((event) => (
              <li key={event.id}>
                <Link
                  to="/events/$eventId"
                  params={{ eventId: event.id }}
                  className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-accent/50"
                >
                  <span className="font-medium">{event.name}</span>
                  <Badge variant="muted">{event.kind}</Badge>
                  {event.source === 'calendar' ? <Badge variant="secondary">calendar</Badge> : null}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatDateTime(event.startsAt) ?? 'no date'} · {event.contactCount} people
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="px-4 pb-2">
          <Button size="sm" onClick={() => setCreating(true)}>
            New event
          </Button>
        </div>

        <CreateEventDialog open={creating} onOpenChange={setCreating} />

        <NotWired
          title="Search and date filtering"
          blockedOn="events.list honours q / kind / from / to server-side; the controls are not built in this track"
        />
      </PageBody>
    </>
  );
}
